import { Query } from '@apollo/client/react/components';
import { KeySet } from '@tableau/data-structures';
import React from 'react';

import {
  VirtualConnectionTableLineageCountsFilteredByColumnQuery, VirtualConnectionTableLineageCountsFilteredByColumnQueryVariables, VirtualConnectionTableLineageCountsQuery, VirtualConnectionTableLineageCountsQueryVariables,
  VirtualConnectionTableLineageCountsFilteredByColumnDocument,
  VirtualConnectionTableLineageCountsDocument
} from '../../../../../gen-src/queries';
import { FeatureFlagsContext } from '../../../data-catalog-shared';
import { LineageCountsParentProps } from '../lineage/LineageCountsCacher';
import { renderFilteredLineageCounts, renderUnfilteredLineageCounts } from '../lineage/LineageCountsFunctions';


export type VirtualConnectionTableLineageCounts = {
  readonly filteredFieldName: string | null;
  readonly columns: number;
  readonly upstreamDatabases: number;
  readonly upstreamTables: number;
  readonly upstreamVirtualConnections: number;
  readonly downstreamFlows: number;
  readonly downstreamDatasources: number;
  readonly downstreamLenses: number;
  readonly downstreamWorkbooks: number;
  readonly downstreamSheets: number;
  readonly downstreamDashboards: number;
  readonly downstreamMetrics: number;
  readonly downstreamOwners: number;
};

function createUnfilteredCounts(data: VirtualConnectionTableLineageCountsQuery): VirtualConnectionTableLineageCounts | null {
  const virtualConnectionTable = data.virtualConnectionTables && data.virtualConnectionTables[0];
  if (virtualConnectionTable
    && virtualConnectionTable.unfilteredColumnsConnection
    && virtualConnectionTable.upstreamDatabasesConnection
    && virtualConnectionTable.upstreamTablesConnection
    && virtualConnectionTable.upstreamVirtualConnectionsConnection
    && virtualConnectionTable.downstreamFlowsConnection
    && virtualConnectionTable.downstreamDatasourcesConnection
    && virtualConnectionTable.downstreamWorkbooksConnection
    && virtualConnectionTable.downstreamSheetsConnection
    && virtualConnectionTable.downstreamDashboardsConnection
    && virtualConnectionTable.downstreamMetricsConnection
    && virtualConnectionTable.downstreamOwnersConnection
  ) {
    return {
      filteredFieldName: null,
      columns: virtualConnectionTable.unfilteredColumnsConnection.totalCount,
      upstreamDatabases: virtualConnectionTable.upstreamDatabasesConnection.totalCount,
      upstreamTables: virtualConnectionTable.upstreamTablesConnection.totalCount,
      upstreamVirtualConnections: virtualConnectionTable.upstreamVirtualConnectionsConnection.totalCount,
      downstreamFlows: virtualConnectionTable.downstreamFlowsConnection.totalCount,
      downstreamDatasources: virtualConnectionTable.downstreamDatasourcesConnection.totalCount,
      downstreamWorkbooks: virtualConnectionTable.downstreamWorkbooksConnection.totalCount,
      downstreamLenses: virtualConnectionTable.downstreamLensesConnection?.totalCount || 0,
      downstreamSheets: virtualConnectionTable.downstreamSheetsConnection.totalCount,
      downstreamDashboards: virtualConnectionTable.downstreamDashboardsConnection.totalCount,
      downstreamMetrics: virtualConnectionTable.downstreamMetricsConnection.totalCount,
      downstreamOwners: virtualConnectionTable.downstreamOwnersConnection.totalCount,
    };
  }
  return null;
}

function createFilteredCounts(data: VirtualConnectionTableLineageCountsFilteredByColumnQuery): VirtualConnectionTableLineageCounts | null {
  const virtualConnectionTable = data.virtualConnectionTables && data.virtualConnectionTables[0];
  const filteredColumn = virtualConnectionTable && virtualConnectionTable.filteredColumnsConnection && virtualConnectionTable.filteredColumnsConnection.nodes[0];
  if (virtualConnectionTable
    && virtualConnectionTable.unfilteredColumnsConnection
    && filteredColumn
    && filteredColumn.upstreamDatabasesConnection
    && filteredColumn.upstreamTablesConnection
    && filteredColumn.upstreamVirtualConnectionsConnection
    && filteredColumn.downstreamFlowsConnection
    && filteredColumn.downstreamDatasourcesConnection
    && filteredColumn.downstreamWorkbooksConnection
    && filteredColumn.downstreamSheetsConnection
    && filteredColumn.downstreamDashboardsConnection
    && filteredColumn.downstreamMetricsConnection
    && filteredColumn.downstreamOwnersConnection
  ) {
    return {
      filteredFieldName: filteredColumn?.name ?? null,
      columns: virtualConnectionTable.unfilteredColumnsConnection.totalCount,
      upstreamDatabases: filteredColumn.upstreamDatabasesConnection.totalCount,
      upstreamTables: filteredColumn.upstreamTablesConnection.totalCount,
      upstreamVirtualConnections: filteredColumn.upstreamVirtualConnectionsConnection.totalCount,
      downstreamFlows: filteredColumn.downstreamFlowsConnection.totalCount,
      downstreamDatasources: filteredColumn.downstreamDatasourcesConnection.totalCount,
      downstreamLenses: filteredColumn.downstreamLensesConnection?.totalCount || 0,
      downstreamWorkbooks: filteredColumn.downstreamWorkbooksConnection.totalCount,
      downstreamSheets: filteredColumn.downstreamSheetsConnection.totalCount,
      downstreamDashboards: filteredColumn.downstreamDashboardsConnection.totalCount,
      downstreamMetrics: filteredColumn.downstreamMetricsConnection.totalCount,
      downstreamOwners: filteredColumn.downstreamOwnersConnection.totalCount,
    };
  }
  return null;
}

export type VirtualConnectionTableLineageCountsQueryWrapperProps = {
  readonly vizportalUrlId: string;
  readonly selectedColumnKeys: KeySet;
} & LineageCountsParentProps<VirtualConnectionTableLineageCounts>;

export class VirtualConnectionTableLineageCountsQueryWrapper extends React.Component<VirtualConnectionTableLineageCountsQueryWrapperProps> {

  public override render(): React.ReactNode {
    const selectedFields = Object.keys(this.props.selectedColumnKeys);
    if (selectedFields.length > 0) {
      const selectedColumnId = selectedFields[0];
      return (
        <FeatureFlagsContext.Consumer>
          {() =>
            <Query<VirtualConnectionTableLineageCountsFilteredByColumnQuery, VirtualConnectionTableLineageCountsFilteredByColumnQueryVariables>
              query={VirtualConnectionTableLineageCountsFilteredByColumnDocument}
              variables={{
                vizportalUrlId: this.props.vizportalUrlId,
                selectedColumnId: selectedColumnId,
              }}
            >
              {result => <>{renderFilteredLineageCounts(result, createUnfilteredCounts, createFilteredCounts, this.props.children)}</>}
            </Query>}
        </FeatureFlagsContext.Consumer>
      );
    } else {
      return (
        <FeatureFlagsContext.Consumer>
          {() =>
            <Query<VirtualConnectionTableLineageCountsQuery, VirtualConnectionTableLineageCountsQueryVariables>
              query={VirtualConnectionTableLineageCountsDocument}
              variables={{
                vizportalUrlId: this.props.vizportalUrlId,
              }}
            >
              {result => <>{renderUnfilteredLineageCounts(result, createUnfilteredCounts, this.props.children)}</>}
            </Query>}
        </FeatureFlagsContext.Consumer>
      );
    }
  }

}

